import React from 'react';
import CharacterAttributes from './CharacterAttributes';
import {
  CharacterCreationContainer,
  PageTitle,
  PageDescription,
} from './CharacterStyles';
import { ATTRIBUTE_GROUPS } from './characterConstants';
import CharacterSummary from './CharacterSummary';
import ProgressIndicatorComponent from './ProgressIndicator';
import useCharacterCreationUtils from './hooks/useCharacterCreationUtils';
import useCreateCharacter from './hooks/useCreateCharacter';
import CharacterCreationNavigation from './CharacterCreationNavigation';

interface CharacterCreationPageContentProps {
  setShowCreatedCharacterModal: (show: boolean) => void;
}

const CharacterCreationPageContent: React.FC<
  CharacterCreationPageContentProps
> = ({ setShowCreatedCharacterModal }) => {
  const {
    currentStep,
    progress,
    totalGroups,
    handleAttributeChange,
    randomizeAttribute,
    goToPreviousStep,
    goToNextStep,
  } = useCharacterCreationUtils();
  const { loading, createCharacter } = useCreateCharacter();
  const isLastStep = currentStep === totalGroups - 1;
  const showPageHero = currentStep === 0;

  // Handle creation
  const onCreateCharacter = () => {
    createCharacter();
    setShowCreatedCharacterModal(true);
  };

  return (
    <CharacterCreationContainer>
      {showPageHero && (
        <>
          <PageTitle>Character Creation</PageTitle>
          <PageDescription>
            Create your character by selecting attributes. You can leave some
            attributes blank for the AI to fill in. After creation, you can chat
            with your character and create more images with them.
          </PageDescription>
        </>
      )}

      <ProgressIndicatorComponent
        currentStep={currentStep}
        totalGroups={totalGroups}
        progress={progress}
        currentGroupLabel={ATTRIBUTE_GROUPS[currentStep]?.label}
      />

      <CharacterAttributes
        onAttributeChange={handleAttributeChange}
        onRandomizeAttribute={randomizeAttribute}
        currentStep={currentStep}
      />

      {isLastStep && <CharacterSummary onCreateCharacter={onCreateCharacter} />}

      <CharacterCreationNavigation
        currentStep={currentStep}
        isLastStep={isLastStep}
        loading={loading}
        goToPreviousStep={goToPreviousStep}
        goToNextStep={goToNextStep}
        onCreateCharacter={onCreateCharacter}
      />
    </CharacterCreationContainer>
  );
};

export default CharacterCreationPageContent;
